/**
 * GPU preference types for WebGL renderer
 */
export type GpuPreference = 'auto' | 'high-performance' | 'default';

/**
 * Result of GPU detection or preference resolution
 */
export interface GpuDetectionResult {
	/** WebGL powerPreference value */
	powerPreference: 'high-performance' | 'default';
	/** Recommended quality level based on GPU capabilities */
	defaultQuality: 7 | 10;
}

/**
 * Detects GPU type and returns appropriate settings.
 * Uses WEBGL_debug_renderer_info extension to identify GPU.
 *
 * @returns Detection result with powerPreference and recommended quality
 */
export const detectGpuCapabilities = (): GpuDetectionResult => {
	try {
		const testCanvas = document.createElement('canvas');
		const gl = testCanvas.getContext('webgl', { failIfMajorPerformanceCaveat: false });

		if (!gl) {
			console.info('WebGL not available for GPU detection, using default');
			return { powerPreference: 'default', defaultQuality: 7 };
		}

		const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
		if (!debugInfo) {
			console.info('WEBGL_debug_renderer_info not available, using default');
			return { powerPreference: 'default', defaultQuality: 7 };
		}

		const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
		const vendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string;

		console.info(`GPU Detection - Renderer: ${renderer}, Vendor: ${vendor}`);

		const rendererLower = renderer.toLowerCase();
		const vendorLower = vendor.toLowerCase();

		// Check for dedicated GPU indicators
		const dedicatedGpuPatterns = [
			'nvidia', 'geforce', 'rtx', 'gtx', 'quadro',
			'radeon', 'rx ', 'vega', 'navi',
			'arc ', 'a770', 'a750', 'a380'
		];

		for (const pattern of dedicatedGpuPatterns) {
			if (rendererLower.includes(pattern) || vendorLower.includes(pattern)) {
				console.info('Detected dedicated GPU, using high-performance mode');
				return { powerPreference: 'high-performance', defaultQuality: 10 };
			}
		}

		// Check for integrated/software renderer indicators
		const integratedPatterns = [
			'intel', 'uhd', 'iris',
			'swiftshader', 'llvmpipe', 'mesa', 'software',
			'microsoft basic', 'remote desktop'
		];

		for (const pattern of integratedPatterns) {
			if (rendererLower.includes(pattern)) {
				console.info('Detected integrated/software renderer, using default mode');
				return { powerPreference: 'default', defaultQuality: 7 };
			}
		}

		// If AMD but not Radeon (could be APU)
		if (vendorLower.includes('amd') || vendorLower.includes('ati')) {
			if (rendererLower.includes('radeon')) {
				console.info('Detected AMD Radeon GPU, using high-performance mode');
				return { powerPreference: 'high-performance', defaultQuality: 10 };
			}
			console.info('Detected AMD APU/integrated, using default mode');
			return { powerPreference: 'default', defaultQuality: 7 };
		}

		// Apple Silicon GPUs - they're integrated but powerful
		if (vendorLower.includes('apple')) {
			console.info('Detected Apple GPU, using high-performance mode');
			return { powerPreference: 'high-performance', defaultQuality: 10 };
		}

		console.info('Unknown GPU type, defaulting to default mode');
		return { powerPreference: 'default', defaultQuality: 7 };
	} catch (e) {
		console.info('Error during GPU detection:', e, ', using default');
		return { powerPreference: 'default', defaultQuality: 7 };
	}
};

/**
 * Resolves GPU preference to actual settings.
 * - 'auto': Performs GPU detection
 * - 'high-performance': Returns high-performance settings with quality 10
 * - 'default': Returns default settings with quality 7
 *
 * @param preference - GPU preference setting
 * @returns Resolved detection result
 */
export const resolveGpuPreference = (preference: GpuPreference): GpuDetectionResult => {
	switch (preference) {
		case 'auto':
			return detectGpuCapabilities();
		case 'high-performance':
			return { powerPreference: 'high-performance', defaultQuality: 10 };
		case 'default':
		default:
			return { powerPreference: 'default', defaultQuality: 7 };
	}
};
